import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Appointment, Office, SlotsResponse } from '../../lib/types';
import { ErrorNote, Field, StatusBadge } from '../../ui/components';
import { BookAppointmentForm } from './BookAppointmentForm';

type LookupMode = 'qr' | 'id';

export function CheckInPanel({ office }: { office: Office }) {
  const { persona } = useAuth();
  const canOperate = persona?.roles.includes('COUNTER_OFFICER') ?? false;

  const [mode, setMode] = useState<LookupMode>('qr');
  const [qrInput, setQrInput] = useState('');
  const [idInput, setIdInput] = useState('');
  const [appt, setAppt] = useState<Appointment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookupById = async () => {
    setBusy(true);
    setError(null);
    try {
      const a = await api.get<Appointment>(`/api/v1/servicedesk/appointments/${idInput.trim()}`);
      setAppt(a);
    } catch (e) {
      setAppt(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  const checkInByToken = async (token: string) => {
    setBusy(true);
    setError(null);
    try {
      const a = await api.post<Appointment>(`/api/v1/servicedesk/appointments/${token}/check-in`);
      setAppt(a);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Check-in failed');
    } finally {
      setBusy(false);
    }
  };

  const act = async (action: 'start-serving' | 'complete' | 'no-show' | 'cancel') => {
    if (!appt) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<Appointment>(`/api/v1/servicedesk/appointments/${appt.id}/${action}`);
      setAppt(updated);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Look up an appointment</h2>

        <div className="mb-3 flex gap-1 rounded-lg bg-slate-200/60 p-1 text-sm">
          <button
            className={`flex-1 rounded-md px-3 py-1.5 font-semibold transition ${mode === 'qr' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
            onClick={() => setMode('qr')}
          >
            By QR token
          </button>
          <button
            className={`flex-1 rounded-md px-3 py-1.5 font-semibold transition ${mode === 'id' ? 'bg-white shadow-sm' : 'text-slate-500'}`}
            onClick={() => setMode('id')}
          >
            By appointment ID
          </button>
        </div>

        {error ? (
          <div className="mb-3">
            <ErrorNote message={error} />
          </div>
        ) : null}

        {mode === 'qr' ? (
          <div className="space-y-2">
            <Field label="Scanned / pasted QR token">
              <input className="field-input" value={qrInput} onChange={(e) => setQrInput(e.target.value)} placeholder="qr token…" />
            </Field>
            <button className="btn-primary w-full" onClick={() => checkInByToken(qrInput.trim())} disabled={busy || !qrInput.trim()}>
              {busy ? 'Checking in…' : 'Check in'}
            </button>
            <p className="text-xs text-slate-400">Checks the appointment in directly — only works on the appointment's own date.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Field label="Appointment ID">
              <input className="field-input" value={idInput} onChange={(e) => setIdInput(e.target.value)} placeholder="appointment id…" />
            </Field>
            <button className="btn-secondary w-full" onClick={lookupById} disabled={busy || !idInput.trim()}>
              {busy ? 'Looking up…' : 'Look up'}
            </button>
          </div>
        )}

        {appt ? (
          <div className="mt-4 rounded-lg border border-slate-200 p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">{appt.serviceCode}</div>
              <StatusBadge status={appt.status} />
            </div>
            <div className="text-xs text-slate-500">Citizen: {appt.personId}</div>
            <div className="text-xs text-slate-500">Slot: {formatDateTime(appt.slot)}</div>
            {appt.caseRef ? <div className="text-xs text-slate-500">Case ref: {appt.caseRef}</div> : null}
            <div className="mt-1 text-xs text-slate-400">
              QR: <code>{appt.qrToken}</code>
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {appt.status === 'BOOKED' ? (
                <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => checkInByToken(appt.qrToken)} disabled={busy}>
                  Check in
                </button>
              ) : null}
              {appt.status === 'CHECKED_IN' ? (
                <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act('start-serving')} disabled={busy || !canOperate}>
                  Start serving
                </button>
              ) : null}
              {appt.status === 'SERVING' ? (
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act('complete')} disabled={busy || !canOperate}>
                  Complete
                </button>
              ) : null}
              {appt.status === 'BOOKED' || appt.status === 'CHECKED_IN' ? (
                <>
                  <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => act('no-show')} disabled={busy || !canOperate}>
                    No-show
                  </button>
                  <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => act('cancel')} disabled={busy}>
                    Cancel
                  </button>
                </>
              ) : null}
            </div>

            {appt.status === 'BOOKED' ? <ReschedulePanel office={office} appt={appt} onRescheduled={setAppt} /> : null}
          </div>
        ) : null}
      </div>

      <BookAppointmentForm office={office} />
    </div>
  );
}

function ReschedulePanel({ office, appt, onRescheduled }: { office: Office; appt: Appointment; onRescheduled: (a: Appointment) => void }) {
  const [date, setDate] = useState(appt.slot.slice(0, 10));
  const [slots, setSlots] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchSlots = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<SlotsResponse>(
        `/api/v1/servicedesk/appointments/slots?officeCode=${office.code}&serviceCode=${appt.serviceCode}&from=${date}&to=${date}`,
      );
      setSlots(res.slots);
    } catch (e) {
      setSlots(null);
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not load slots');
    } finally {
      setLoading(false);
    }
  };

  const reschedule = async (slot: string) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await api.post<Appointment>(`/api/v1/servicedesk/appointments/${appt.id}/reschedule`, { slot });
      onRescheduled(updated);
      setSlots(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Reschedule failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Reschedule</div>
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex items-center gap-2">
        <input type="date" className="field-input !w-auto" value={date} onChange={(e) => { setDate(e.target.value); setSlots(null); }} />
        <button className="btn-secondary !px-2.5 !py-1.5 text-xs" onClick={fetchSlots} disabled={loading}>
          {loading ? 'Loading…' : `Slots on ${formatDate(date)}`}
        </button>
      </div>
      {slots ? (
        slots.length === 0 ? (
          <div className="mt-2 text-xs text-slate-400">No open slots that day.</div>
        ) : (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {slots.map((s) => (
              <button
                key={s}
                onClick={() => reschedule(s)}
                disabled={loading}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold tabular-nums hover:border-brand-400"
              >
                {new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
              </button>
            ))}
          </div>
        )
      ) : null}
    </div>
  );
}
